/**
 * Local-date helpers for Specials (date-windowed promotions).
 *
 * Effective dates are 'YYYY-MM-DD' calendar dates. They are parsed by
 * splitting the components and constructing a LOCAL Date — never
 * `new Date("YYYY-MM-DD")`, which UTC-parses and shifts the visible day
 * in negative UTC offsets.
 */

import moment from "moment-timezone";
import { ServicePackage } from "../../../types/packages";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Parse 'YYYY-MM-DD' into a local-midnight Date. Null for bad input. */
export function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parts = value.slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const [y, mo, d] = parts.map((p) => Number(p));
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

/**
 * "Jul 1" style display for a 'YYYY-MM-DD' calendar date. Formats straight
 * from the string components — no Date/moment round-trip, so no timezone
 * (or moment-timezone default-zone) shift can move the visible day.
 */
export function formatLocalDate(value: string | null | undefined): string {
  if (!value) return "";
  const parts = value.slice(0, 10).split("-");
  if (parts.length !== 3) return "";
  const [, mo, d] = parts.map((p) => Number(p));
  if (!mo || !d || mo < 1 || mo > 12) return "";
  return `${MONTH_NAMES[mo - 1]} ${d}`;
}

/** "Special · Jul 1 – Jul 31" badge label for a special's window. */
export function formatSpecialWindow(pkg: ServicePackage): string {
  const start = formatLocalDate(pkg.effective_start_date);
  const end = formatLocalDate(pkg.effective_end_date);
  if (!start || !end) return "Special";
  return `Special · ${start} – ${end}`;
}

export type SpecialStatus = "active" | "upcoming" | "past";

/**
 * Classify a special against "today" on the SALON's calendar:
 * start <= today <= end → active; start > today → upcoming; end < today → past.
 * Missing/invalid dates fall back to active so the row stays visible.
 *
 * `timezone` is the salon's IANA zone (`businessInfo.timezone`); "today" is
 * derived from it rather than the browser's clock, so a special flips
 * active/upcoming/past on the business's own local day — consistent with the
 * backend sell/purchase gates. Falls back to the browser day when timezone is
 * absent (still loading). `now` is injectable for deterministic tests.
 */
export function classifySpecial(
  pkg: ServicePackage,
  timezone?: string | null,
  now: Date = new Date(),
): SpecialStatus {
  const start = parseLocalDate(pkg.effective_start_date);
  const end = parseLocalDate(pkg.effective_end_date);
  const salonNow = timezone ? moment.tz(now, timezone) : moment(now);
  const todayLocal = new Date(
    salonNow.year(),
    salonNow.month(),
    salonNow.date(),
  );
  if (start && start.getTime() > todayLocal.getTime()) return "upcoming";
  if (end && end.getTime() < todayLocal.getTime()) return "past";
  return "active";
}
